/*
  Enquiry List — multi-item quote request for B2B mode.

  A buyer taps "Add to enquiry" on any product card / product page. Items are
  stored in localStorage. A floating launcher shows the count and opens a drawer
  listing every shortlisted piece with a remove control and one shared quote form.

  Submission reuses the SAME reliable path as the single-item enquire modal:
  a native POST to /contact (Shopify sends the notification email), with the full
  item list serialised into the message body. After Shopify reloads the page with
  ?contact_posted=true we show a confirmation toast and clear the list.
*/
(function () {
  "use strict";

  var STORAGE_KEY = "vianne_enquiry_list";
  var SUBMIT_FLAG = "vianneQuoteSubmitted";

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {}
    document.dispatchEvent(new CustomEvent("enquiry-list:change"));
  }

  function addItem(item) {
    if (!item || !item.id) return;
    var items = read();
    if (items.some(function (i) { return i.id === item.id; })) return; // no dupes
    items.push(item);
    write(items);
  }

  function removeItem(id) {
    write(read().filter(function (i) { return i.id !== id; }));
  }

  function has(id) {
    return read().some(function (i) { return i.id === id; });
  }

  function init() {
    maybeShowConfirmation();

    var launcher = document.querySelector(".enquiry-list__launcher");
    var drawer = document.querySelector("[data-enquiry-list-drawer]");

    // Reflect add/remove state on every trigger button, everywhere on the page.
    function syncTriggers() {
      var items = read();
      var count = items.length;
      document.querySelectorAll("[data-add-to-list]").forEach(function (btn) {
        var id = btn.getAttribute("data-product-id");
        var active = items.some(function (i) { return i.id === id; });
        btn.setAttribute("aria-pressed", active ? "true" : "false");
        btn.classList.toggle("is-in-list", active);
        var label = btn.querySelector("[data-add-to-list-label]");
        if (label) label.textContent = active ? "Added ✓" : (btn.getAttribute("data-label-default") || "Add to enquiry");
      });
      document.querySelectorAll("[data-enquiry-list-count]").forEach(function (el) {
        el.textContent = String(count);
        el.classList.toggle("is-zero", count === 0);
      });
      if (launcher) launcher.style.display = count > 0 ? "inline-flex" : "none";
      // Lift the theme's back-to-top button above the launcher (CSS hook in enquiry-list.liquid).
      document.body.classList.toggle("has-enquiry-list", count > 0);
    }

    function renderDrawer() {
      if (!drawer) return;
      var items = read();
      var list = drawer.querySelector("[data-enquiry-list-items]");
      var empty = drawer.querySelector("[data-enquiry-list-empty]");
      var form = drawer.querySelector("[data-enquiry-list-form]");
      if (empty) empty.classList.toggle("hidden", items.length > 0);
      if (form) form.classList.toggle("hidden", items.length === 0);
      if (!list) return;
      list.innerHTML = "";
      items.forEach(function (item) {
        var li = document.createElement("li");
        li.className = "enquiry-list__item flex items-center gap-3 py-3";
        var img = item.image
          ? '<img src="' + escapeAttr(item.image) + '" alt="" class="w-14 h-14 object-cover rounded-sm bg-foreground/5" width="56" height="56">'
          : '<span class="w-14 h-14 rounded-sm bg-foreground/5 inline-block"></span>';
        li.innerHTML =
          img +
          '<span class="min-w-0 flex-1">' +
          '<span class="block text-sm truncate">' + escapeHtml(item.title) + "</span>" +
          '<span class="block text-2xs opacity-60 mt-0.5">SKU: ' + escapeHtml(item.sku || "—") + "</span>" +
          "</span>" +
          '<button type="button" class="enquiry-list__remove p-2 opacity-60 hover:opacity-100" data-enquiry-list-remove="' +
          escapeAttr(item.id) + '" aria-label="Remove ' + escapeAttr(item.title) + '">&times;</button>';
        list.appendChild(li);
      });
    }

    var closeTimer = null;

    function openDrawer() {
      renderDrawer();
      if (!drawer) return;
      // Reopening mid-close: cancel the pending close so the drawer slides back in.
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      drawer.classList.remove("is-closing");
      if (typeof drawer.showModal === "function") {
        if (!drawer.open) drawer.showModal();
      } else { drawer.setAttribute("open", ""); drawer.style.display = "block"; }
    }
    function closeDrawer() {
      if (!drawer) return;
      if (drawer.classList.contains("is-closing")) return;
      var reduceMotion = false;
      try { reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}
      if (typeof drawer.close === "function" && drawer.open) {
        if (reduceMotion) { drawer.close(); drawer.style.display = ""; return; }
        // Play the slide-out before actually closing the dialog.
        drawer.classList.add("is-closing");
        closeTimer = setTimeout(function () {
          closeTimer = null;
          drawer.close();
          drawer.classList.remove("is-closing");
          drawer.style.display = "";
        }, 380);
      } else {
        drawer.removeAttribute("open");
        drawer.style.display = "";
      }
    }

    // Global click delegation: add triggers, launcher, remove, close.
    document.addEventListener("click", function (event) {
      var add = event.target.closest("[data-add-to-list]");
      if (add) {
        event.preventDefault();
        var id = add.getAttribute("data-product-id");
        if (has(id)) {
          removeItem(id);
        } else {
          addItem({
            id: id,
            title: add.getAttribute("data-product-title") || "",
            sku: add.getAttribute("data-product-sku") || "",
            url: add.getAttribute("data-product-url") || "",
            image: add.getAttribute("data-product-image") || "",
          });
        }
        return;
      }
      if (event.target.closest("[data-enquiry-list-open]")) { event.preventDefault(); openDrawer(); return; }
      if (event.target.closest("[data-enquiry-list-close]")) { event.preventDefault(); closeDrawer(); return; }
      var rm = event.target.closest("[data-enquiry-list-remove]");
      if (rm) { event.preventDefault(); removeItem(rm.getAttribute("data-enquiry-list-remove")); return; }
    });

    if (drawer) {
      drawer.addEventListener("click", function (event) {
        if (event.target === drawer) closeDrawer();
      });
      // Escape closes a dialog instantly by default; route it through the animation.
      drawer.addEventListener("cancel", function (event) {
        event.preventDefault();
        closeDrawer();
      });
      var form = drawer.querySelector("[data-enquiry-list-form]");
      if (form) form.addEventListener("submit", handleSubmit);
    }

    function handleSubmit(event) {
      // Do NOT preventDefault — let the native POST to /contact fire the email.
      try {
        var items = read();
        if (!items.length) { event.preventDefault(); return; }
        var lines = items.map(function (i, n) {
          return (n + 1) + ". " + i.title + " (SKU: " + (i.sku || "—") + ")" + (i.url ? " — " + i.url : "");
        });
        var itemsInput = drawer.querySelector("[data-enquiry-list-items-input]");
        var countInput = drawer.querySelector("[data-enquiry-list-count-input]");
        var bodyField = form.querySelector('textarea[name="contact[body]"]');
        if (itemsInput) itemsInput.value = lines.join("\n");
        if (countInput) countInput.value = String(items.length);
        if (bodyField) {
          var note = bodyField.value ? bodyField.value + "\n\n" : "";
          bodyField.value = note + "Requested pieces (" + items.length + "):\n" + lines.join("\n");
        }
        try { sessionStorage.setItem(SUBMIT_FLAG, "1"); } catch (e) {}
        var btn = form.querySelector("[data-enquiry-list-submit]");
        if (btn) { btn.disabled = true; btn.textContent = "Sending…"; }
      } catch (err) {
        console.warn("[enquiry-list] pre-submit hook failed (non-fatal)", err);
      }
    }

    document.addEventListener("enquiry-list:change", function () {
      syncTriggers();
      renderDrawer();
    });
    syncTriggers();
  }

  function maybeShowConfirmation() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get("contact_posted") !== "true") return;
      if (sessionStorage.getItem(SUBMIT_FLAG) !== "1") return;
      sessionStorage.removeItem(SUBMIT_FLAG);
      localStorage.removeItem(STORAGE_KEY); // clear the list after a successful quote request
      showToast("Thanks — we received your quote request. Our team will reply with pricing and lead times shortly.");
    } catch (e) {}
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  function showToast(message) {
    var toast = document.createElement("div");
    toast.setAttribute("role", "status");
    toast.textContent = message;
    toast.style.cssText = [
      "position:fixed", "left:50%", "bottom:24px", "transform:translateX(-50%)",
      "z-index:2147483000", "max-width:min(92vw,420px)", "background:#123016",
      "color:#f4dfb2", "padding:14px 18px", "border:1px solid rgba(244,223,178,.35)",
      "border-radius:8px", "font-size:14px", "line-height:1.4", "box-shadow:0 12px 30px rgba(0,0,0,.25)",
    ].join(";");
    document.body.appendChild(toast);
    setTimeout(function () {
      toast.style.transition = "opacity .4s ease";
      toast.style.opacity = "0";
      setTimeout(function () { toast.remove(); }, 450);
    }, 5000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
